import * as React from 'react';
import { goBack } from 'react-router-redux';
import { connect } from 'react-redux';
import { range, merge } from 'lodash';
import moment = require('moment');

import { getDriver, mergeDrivers } from '../../redux/actions/drivers';
import {
  Card,
  IconButton,
  Notification,
  MergeForm,
  RouteWrapper,
  HeadingSection,
  Section,
} from '../../components';

interface Props {
  match: any;
  goBack: () => void;
  mergeDrivers: (driverIds: Array<number>, overrides?) => Promise<any>;
  getDriver: (id: number) => Promise<any>;
}

interface State {
  id: number;
  mergeDriverId: number;
  isFetching: boolean;
  drivers: Array<any>;
  selectedFields: number[];
  error: string | boolean;
}

class Route extends React.Component<Props, State> {
  state = {
    drivers: [],
    isFetching: true,
    id: Number(this.props.match.params.id),
    mergeDriverId: Number(this.props.match.params.mergeDriverId),
    selectedFields: range(0, Route.FIELDS.length, 0),
    error: false,
  };

  private static CUSTOM_LABELS = {
    company_id: 'company',
    pass_date: 'license pass date',
    expiry_date: 'license expiry date',
    convictions_1:
      'Have they had any motoring convictions in the last 5 years or are there any prosecutions pending or police enquiries outstanding (including fixed penalty offences)?', //tslint:disable-line
    convictions_2:
      'Have they had any criminal convictions (or been charged with a criminal offence but not yet tried)?', //tslint:disable-line
    convictions_3: 'Convictions Details',
    accidents_years: 'Years of accidents',
    accidents_total_number: 'Total number of accidents, thefts, or losses',
    accidents_total_cost: 'Total cost of the losses, and settled claims',
    accidents_outstanding_number: 'Number of outstanding losses and claims',
    accidents_outstanding_cost:
      'Estimated cost of outstanding losses and claims',
  };

  private static FIELDS: string[] = [
    'title',
    'name',
    'first_name',
    'middle_name',
    'last_name',
    'phone_number',
    'email',
    'date_of_birth',
    'company_id',
    'occupation',
    'driving_license_number',
    'pass_date',
    'expiry_date',
    'reputation',
    'postcode',
    'address',
    'address_2',
    'convictions_1',
    'convictions_2',
    'convictions_3',
    'accidents',
    'accidents_years',
    'accidents_total_number',
    'accidents_total_cost',
    'accidents_outstanding_number',
    'accidents_outstanding_cost',
    'defects',
    'defects_details',
    'county',
    'city',
  ];

  componentDidMount() {
    this.getDrivers();
  }

  private async getDrivers() {
    const drivers = await Promise.all([
      this.props.getDriver(this.state.id),
      this.props.getDriver(this.state.mergeDriverId),
    ]);

    if (
      drivers.filter((driver) => driver.error || driver.data.deleted_at).length
    ) {
      this.setState({
        error: 'The drivers selected do not exist',
      });
    } else {
      this.setState({
        isFetching: false,
        drivers: drivers.map(({ data }) => data),
      });
    }
  }

  private setSelectedField = (index, driverIndex) => {
    const selectedFields = [...this.state.selectedFields];
    selectedFields[index] = driverIndex;
    this.setState({
      selectedFields,
    });
  };

  private getReputationName = (reputation) => {
    switch (reputation) {
      case 0:
        return 'Good';
      case 1:
        return 'Moderate';
      default:
        return 'Bad';
    }
  };

  private getYesNo = (value) => {
    return value ? 'Yes' : 'No';
  };

  private selectAll = (driverIndex) => {
    this.setState({
      selectedFields: range(driverIndex, driverIndex + Route.FIELDS.length, 0),
    });
  };

  private changeFormValues = (driver) => {
    if (!driver) {
      return {};
    }

    return merge({}, driver, {
      pass_date: moment(driver.pass_date).calendar(),
      expiry_date: moment(driver.expiry_date).calendar(),
      date_of_birth: moment(driver.date_of_birth).calendar(),
      company_id: (driver.company || { name: '' }).name,
      reputation: this.getReputationName(driver.reputation),
      convictions_1: this.getYesNo(driver.convictions_1),
      convictions_2: this.getYesNo(driver.convictions_1),
      accidents: this.getYesNo(driver.accidents),
      defects: this.getYesNo(driver.defects),
    });
  };

  private get mergedValues() {
    const merged = {};
    Route.FIELDS.forEach((field, index) => {
      const value = this.state.drivers[this.state.selectedFields[index]][field];
      if (value !== null) {
        merged[field] = value;
      }
    });
    return merged;
  }

  private merge = async () => {
    if (!window.confirm('Are you sure you wish to merge these two drivers?')) {
      return;
    }

    const { error } = await this.props.mergeDrivers(
      [this.state.id, this.state.mergeDriverId],
      this.mergedValues,
    );

    if (error) {
      this.setState({ error });
    } else {
      this.props.goBack();
    }
  };

  render() {
    return (
      <RouteWrapper
        requireAuth
        requirePermission="MANAGE_DRIVERS"
        title="Merge Drivers"
        ready={!this.state.isFetching}
        error={this.state.isFetching && this.state.error}
      >
        <HeadingSection
          title="Merge Drivers"
          controls={[
            <IconButton
              key="return"
              icon="undo"
              className="is-default is-pulled-right"
              handleClick={() => {
                this.props.goBack();
              }}
            >
              Return
            </IconButton>,
          ]}
        />
        <Section>
          <div className="columns">
            {[0, 1].map((driverIndex) => (
              <div className="column is-6" key={driverIndex}>
                <div className="level">
                  <div className="level-left">
                    <div className="level-item">
                      <strong>
                        {this.state.drivers[driverIndex] &&
                          this.state.drivers[driverIndex].name}
                      </strong>
                    </div>
                  </div>
                  <div className="level-right">
                    <div className="level-item">
                      <button
                        className="is-default"
                        onClick={() => this.selectAll(driverIndex)}
                      >
                        Select All
                      </button>
                    </div>
                  </div>
                </div>
                <MergeForm
                  fields={Route.FIELDS}
                  customLabels={Route.CUSTOM_LABELS}
                  values={this.changeFormValues(
                    this.state.drivers[driverIndex],
                  )}
                  select={(index: number) => {
                    this.setSelectedField(index, driverIndex);
                  }}
                  isSelected={(index: number) => {
                    return this.state.selectedFields[index] === driverIndex;
                  }}
                />
              </div>
            ))}
          </div>
          {this.state.error && (
            <Notification type="danger">{this.state.error}</Notification>
          )}
          <div className="buttons is-right">
            <button className="is-primary" onClick={this.merge}>
              Merge
            </button>
          </div>
        </Section>
      </RouteWrapper>
    );
  }
}

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  getDriver(driverId: number) {
    return dispatch(getDriver(driverId));
  },
  goBack() {
    return dispatch(goBack());
  },
  mergeDrivers(driverIds, overrides) {
    return dispatch(mergeDrivers(driverIds, overrides));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Route);
